import React, { useEffect, useMemo, useState } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { updateLayerExtra as updateLayerExtraAction } from '../../../../actions/device'
import { updateSettingsViaApi as updateSettingsAction } from '../../../../actions/device'
import { Centered, Column, Confirmation, Headings, Input, Loader } from '../../../../components'
import { MessageOption } from '../../../../components/Screens/Estate/Store/CustomMessage'
import { getCustomMessagesFromLayerExtra, getIsWorking, getStoreCustomMessages, setCustomMessagesIntoLayerExtra } from '../../../../reducers/customMessages'
import { IDUtils } from '../../../../utils'

function isOptionSelected(message, currentValue) {
    return message === currentValue
}
function DeviceMessageModal({ children, device, isWorking, layerExtra, messages, updateLayerExtra, updateSettings }) {
    // Custom messages value
    const [value, setValue] = useState()
    // Flag to set if the initial value has changed
    const [hasChanged, setHasChanged] = useState(false)

    const { t } = useTranslation()

    // If layer extra changes, we update current value of the custom message
    useEffect(() => {
        const customMessages = getCustomMessagesFromLayerExtra(layerExtra)
        setValue(customMessages)
        setHasChanged(false)
    }, [layerExtra])

    /**
     * On Change selected messages
     * @param {Object} param0 event
     */
    const onChange = ({ target: { value: newValue } }) => {
        // Mark as we have changed the initial value
        setHasChanged(true)

        // If value is the same, it means the same value has been clicked, so we toggle it
        setValue(value === newValue ? undefined : newValue)
    }

    /**
     * On Submit new custom messages
     */
    const onSubmit = async () => {
        // If the value has not changed, just confirm. This is util when there's no option to select,
        // or we are still using the initial value. By doing this, we prevent a useless call to the api
        if (!hasChanged) {
            return true
        }

        let customMessages = value

        // If custom messages is an array of messages
        if (value instanceof Array) {
            // We filter values not in the options out
            const filteredOptions = messages.map(({ message }) => message)
            customMessages = value.filter((message) => filteredOptions.includes(message))
        }

        // Create the new layer extra object
        const newLayerExtra = setCustomMessagesIntoLayerExtra(layerExtra, customMessages)

        // Call the api to update the device layer extra field
        const [ok] = await updateLayerExtra(newLayerExtra, device)

        // If it was updated, send a socket command for the device to push settings
        if (ok) {
            await updateSettings(device, IDUtils.generateSocketGroupIDSettingsPush())
        }
    }

    /**
     * Messages options for the input
     */
    const formatMessagesValues = useMemo(
        () =>
            messages.map(({ index, message }) => ({
                name: <MessageOption message={message} selected={isOptionSelected(message, value)} title={t('custom-message-title', { index })} />,
                value: message,
            })),
        [messages, value, t]
    )

    /**
     * Render modal body
     */
    const renderBody = () => {
        // It's calling the api
        if (isWorking) {
            return (
                <Centered horizontal>
                    <Loader />
                </Centered>
            )
        }

        // No messages have been set for this store
        if (!messages.length) {
            return (
                <Column center width="300px">
                    <Headings.H5 italic>{t('custom-messages-empty')}</Headings.H5>
                </Column>
            )
        }

        // Regular form
        return (
            <Column width="300px">
                <Input onChange={onChange} onlyOne type="multiple" value={value} values={formatMessagesValues} />
            </Column>
        )
    }

    return (
        <Confirmation header={t('device-custom-message')} confirmation={renderBody()} ok={messages.length ? t('save-push') : t('ok')} onConfirm={onSubmit}>
            {children}
        </Confirmation>
    )
}

DeviceMessageModal.propTypes = {
    children: PropTypes.node,
    device: PropTypes.string,
    isWorking: PropTypes.bool,
    layerExtra: PropTypes.shape({}),
    messages: PropTypes.arrayOf(PropTypes.shape({})),
    t: PropTypes.func.isRequired,
    updateLayerExtra: PropTypes.string.isRequired,
    updateSettings: PropTypes.string.isRequired,
}

DeviceMessageModal.defaultProps = {
    children: null,
    device: null,
    isWorking: false,
    layerExtra: {},
    messages: [],
}

/**
 * Maps state to props
 * @param {Object} state
 */
function mapStateToProps(state) {
    return {
        isWorking: getIsWorking(state),
        messages: getStoreCustomMessages(state),
    }
}

/**
 * Wraps every action to be dispatched
 * @param {Function} dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            updateLayerExtra: updateLayerExtraAction,
            updateSettings: updateSettingsAction,
        },
        dispatch
    )
}

export default connect(mapStateToProps, mapDispatchToProps)(DeviceMessageModal)
